import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useIsBookPinned, usePinnedBooksLoading, useTogglePinnedBook } from '@/hooks/use-pinned-books';
import { cn } from '@/lib/utils';
import { Pin } from 'lucide-react';
import { Link } from 'react-router';

/** Mirrors zycommon-server Subjects.CoverColors / zycommon-web palette.$cover-colors keys */
export const BOOK_COVER_PALETTE_KEYS = [
    'red',
    'green',
    'orange',
    'yellow',
    'lightblue',
    'indigo',
    'pink',
    'purple',
    'grey',
    'darkgrey',
] as const;

export type BookCoverColor = (typeof BOOK_COVER_PALETTE_KEYS)[number];

export function isBookCoverColor(value: string): value is BookCoverColor {
    return (BOOK_COVER_PALETTE_KEYS as readonly string[]).includes(value);
}

export const DEFAULT_BOOK_COVER_COLOR: BookCoverColor = 'grey';

export interface BookCardBook {
    id: string;
    zybook_id: number;
    variantId?: string;
    title: string;
    development?: boolean;
    coverColor: BookCoverColor;
    subject?: string;
    is_ma_zybook?: boolean;
}

/** Set on the surface that wraps strip/shell/accent so CSS can resolve --book-cover-solid */
export function bookCoverDataAttrs(cover: BookCoverColor): { 'data-book-cover': BookCoverColor } {
    return { 'data-book-cover': cover };
}

export function bookCoverShellClassName(): string {
    return cn('book-cover-shell border');
}

export function bookCoverStripClassName(): string {
    return 'book-cover-strip';
}

export function bookCoverAccentBorderClassName(): string {
    return cn('book-cover-accent border-l-4');
}

interface BookCardProps {
    book: BookCardBook;
    compact?: boolean;
    /** When true, pin state comes from the app-level pinned-books store (per-book subscription). */
    showPin?: boolean;
    /** Controlled pin mode for tests; takes precedence over `showPin`. */
    isPinned?: boolean;
    onTogglePin?: (bookId: string) => void;
}

function BookCardPinButton({
    bookId,
    bookTitle,
    compact,
    isPinned,
    onTogglePin,
    disabled = false,
}: {
    bookId: string;
    bookTitle: string;
    compact: boolean;
    isPinned: boolean;
    onTogglePin: (bookId: string) => void;
    disabled?: boolean;
}) {
    return (
        <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            aria-label={disabled ? `Loading pin state for ${bookTitle}` : isPinned ? `Unpin ${bookTitle}` : `Pin ${bookTitle}`}
            className={cn(
                'border-border/60 bg-background/90 absolute z-10 shadow-sm backdrop-blur-sm transition-opacity cursor-pointer',
                compact
                    ? 'right-1 top-1 size-6'
                    : 'pointer-events-none right-2 top-2 size-7 opacity-0 group-hover:pointer-events-auto group-hover:opacity-100',
            )}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onTogglePin(bookId);
            }}
        >
            <Pin className={cn(compact ? 'size-3.5' : 'size-4', !disabled && isPinned && 'fill-current')} />
        </Button>
    );
}

function StoreBookCardPinButton({ bookId, bookTitle, compact }: { bookId: string; bookTitle: string; compact: boolean }) {
    const loading = usePinnedBooksLoading();
    const isPinned = useIsBookPinned(bookId);
    const togglePin = useTogglePinnedBook();
    return (
        <BookCardPinButton
            bookId={bookId}
            bookTitle={bookTitle}
            compact={compact}
            isPinned={isPinned}
            disabled={loading}
            onTogglePin={togglePin}
        />
    );
}

export function BookCard({ book, compact = false, showPin = false, isPinned = false, onTogglePin }: BookCardProps) {
    return (
        <Card
            {...bookCoverDataAttrs(book.coverColor)}
            className={cn(
                'group relative min-h-0 overflow-hidden py-0 transition-shadow hover:shadow-md cursor-pointer',
                bookCoverShellClassName(),
            )}
        >
            <Link
                to={`/books/${encodeURIComponent(book.id)}`}
                className="focus-visible:ring-ring block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
                <div
                    className={cn('w-full shrink-0', compact ? 'h-2' : 'h-3', bookCoverStripClassName())}
                    aria-hidden
                />
                <div className={cn(compact ? 'px-3 py-2.5 pr-10' : 'px-4 py-4 md:py-5')}>
                    <div className={cn('flex', compact ? 'gap-2' : 'gap-3')}>
                        <span
                            className={cn(
                                'min-w-0 leading-snug break-words',
                                compact ? 'line-clamp-2 text-sm font-medium' : 'text-lg font-semibold',
                            )}
                        >
                            {book.title}
                        </span>
                    </div>
                </div>
            </Link>
            {onTogglePin != null ? (
                <BookCardPinButton
                    bookId={book.id}
                    bookTitle={book.title}
                    compact={compact}
                    isPinned={isPinned}
                    onTogglePin={onTogglePin}
                />
            ) : showPin ? (
                <StoreBookCardPinButton
                    bookId={book.id}
                    bookTitle={book.title}
                    compact={compact}
                />
            ) : null}
        </Card>
    );
}
